import AutoForm, {
    FieldGenerator, FormField,
    FormikProvider,
    useFormContext
} from '@/components/Formik';
import { AddressDisplayFormat, AddressSearchType } from '@/components/Formik/fields/GoogleAddressField';
import { useTranslate } from '@/hooks/useTranslate';
import { Box, Typography } from '@mui/material';

import useMedia from '@/hooks/useMedia';
import moment from 'moment';
import { useFlightFormContext } from '../../layouts/context';

const MapFinderForm = () => {

    // context
    const { isSubmitting, handleSubmit } = useFlightFormContext();
    const { setSubmitting } = useFormContext();

    // hooks
    const { t } = useTranslate('flights');
    const media = useMedia();

    const fields: FormField[] = [

        // add google address field
        FieldGenerator.googleAddress({
            name: 'location_from',
            label: t('finder.form.location_from'),
            props: {
                displayFormat: AddressDisplayFormat.CITY_COUNTRY,
                searchType: AddressSearchType.CITY_OR_REGION,
            }
        }),

        FieldGenerator.googleAddress({
            name: 'location_to',
            label: t('finder.form.location_to'),
            props: {
                displayFormat: AddressDisplayFormat.CITY_COUNTRY,
                searchType: AddressSearchType.CITY_OR_REGION,
            }
        }),

        FieldGenerator.date({
            name: 'start_date',
            label: t('finder.form.start_date'),
            props: {
                disablePast: true,
                maxDate: moment().add(30, 'days').toDate(),
                format: 'YYYY-MM-DD',
            }
        }),

        // add days (slider)
        FieldGenerator.slider({
            name: 'days_range',
            label: t('finder.form.days_range'),
            props: {
                min: 1,
                max: 30,
                step: 1,
            }
        }),

        // max stops
        FieldGenerator.slider({
            name: 'max_stops',
            label: t('finder.form.max_stops'),
            props: {
                min: 0,
                max: 3,
                step: 1,
            }
        }),
    ];

    const submitForm = async (values: Record<string, any>) => {
        setSubmitting(true);
        await handleSubmit({
            location_from: values.location_from,
            location_to: values.location_to,
            start_date: values.start_date,
            days_range: values.days_range,
            max_stops: values.max_stops,
        });
        setSubmitting(false);
    }

    return (
        <Box>
            <Box sx={{
                mb: 7,
            }}>
                <Typography variant="h5">
                    {t('finder.form.title')}
                </Typography>
            </Box>
            <AutoForm
                config={{
                    localStorageKey: 'flights-finder',
                }}
                initialValues={{
                    start_date: moment().format('YYYY-MM-DD'),
                    days_range: 3,
                    max_stops: 1,
                }}
                fields={fields}
                onSubmit={submitForm}
                submitButton={{ children: t('finder.form.submit') }}
                sx={{
                    gap: 5,
                }}
            >
                {({ getField }) => (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: {
                            xs: 3,
                            sm: 5,
                        },

                        '& [data-row]': {
                            ...(media.down.sm && {
                                gap: 3,
                                display: 'flex',
                                flexDirection: 'column',
                            }),
                            ...(media.up.sm && {
                                gap: 5,
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr 1fr',
                            }),
                        }
                    }}>
                        <Box data-row>
                            {getField('location_from')}
                            {getField('location_to')}
                            {getField('start_date')}
                        </Box>
                        <Box data-row>
                            {getField('days_range')}
                            {getField('max_stops')}
                        </Box>
                    </Box>
                )}
            </AutoForm>
        </Box>
    );
};

export default () => (
    <FormikProvider>
        <MapFinderForm />
    </FormikProvider>
)